import { Box } from '@mui/material';
import { Show, useIsMobile } from '@side/pantry';
import { useEffect, useState } from 'react';
import { useResetMapOnUnmount } from '@/common/atoms/community';
import { useAgentTeam } from '@/common/hooks/useAgentTeam/useAgentTeam';
import { Spinner } from '@/components/Spinner';
import { TrackCommunityEvent } from '$analytics/useTrackAnalytics/domains/community/events';
import { useTrackCommunityAnalytics } from '$analytics/useTrackAnalytics/domains/community/useTrackCommunityAnalytics';
import { Header } from './components/Header';
import { MapRenderer } from './components/MapRenderer';
import { PartnerCardList } from './components/PartnerCardList';
import { GEOLOCATION_TIMEOUT, SIDE_HQ_COORDS } from './constants';
import { useGetPartners } from './hooks/useGetPartners';
import { type InitialLocation, LocationType } from './types';
import { mailingAddressHasValidGeolocation } from './utils/location';

export const PartnerMap = () => {
  const isMobile = useIsMobile('md');
  const { team, loading: officeAddressLoading } = useAgentTeam();
  const officeAddress = team?.registeredAddress;
  const [initialCenter, setInitialCenter] = useState<InitialLocation | null>(
    null,
  );
  const { getTeams, partnerGroups, loading } = useGetPartners();
  const trackEvent = useTrackCommunityAnalytics();

  useEffect(() => {
    if (officeAddressLoading || initialCenter) return;

    const setOfficeCoordinatesAsCenter = () => {
      if (mailingAddressHasValidGeolocation(officeAddress)) {
        setInitialCenter({
          lat: Number(officeAddress.geolocationLatitude),
          lng: Number(officeAddress.geolocationLongitude),
          type: LocationType.PRIMARY_OFFICE,
        });
        trackEvent(TrackCommunityEvent.PartnerMapLanding, {
          geoType: LocationType.PRIMARY_OFFICE,
          geoValue: [
            officeAddress.geolocationLatitude,
            officeAddress.geolocationLongitude,
          ],
          geoTeamAddressId: officeAddress.id,
        });
      } else {
        setInitialCenter(SIDE_HQ_COORDS);
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setInitialCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            type: LocationType.CURRENT_LOCATION,
          });
          trackEvent(TrackCommunityEvent.PartnerMapLanding, {
            geoType: LocationType.CURRENT_LOCATION,
            geoValue: [position.coords.latitude, position.coords.longitude],
            geoTeamAddressId: null,
          });
        },
        setOfficeCoordinatesAsCenter,
        { timeout: GEOLOCATION_TIMEOUT },
      );
    } else {
      setOfficeCoordinatesAsCenter();
    }
  }, [officeAddress, officeAddressLoading, trackEvent, initialCenter]);

  useResetMapOnUnmount();

  if (!initialCenter) {
    // Show loading state until initial center is determined
    return <Spinner fullScreen centered />;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Header getTeams={getTeams} initialCenter={initialCenter} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          height: '100%',
          minHeight: 0,
        }}
      >
        <Show when={!isMobile}>
          <PartnerCardList partnerGroups={partnerGroups} loading={loading} />
        </Show>
        <MapRenderer
          getTeams={getTeams}
          partnerGroups={partnerGroups}
          loading={loading}
          initialCenter={initialCenter}
        />
      </Box>
    </Box>
  );
};
